/**
 * Selecao.ts — Estado e logica de selecao de planetas pra lancar frota.
 *
 * Fase 6: a selecao e um CONJUNTO de planetas meus.
 *   click planeta meu          -> seleciona so ele (click no unico selecionado = cancela)
 *   shift+click / modo Varios  -> soma ou tira do conjunto
 *   shift+arrastar             -> retangulo: soma todos os meus planetas dentro
 *   duplo clique / tecla A     -> todos os meus planetas
 *   click em alvo              -> abre o dialogo (uma frota de cada origem)
 *   botao direito / ctrl+click -> ENVIO RAPIDO: manda `pctEnvio` de cada origem, sem dialogo
 *   click em planeta inimigo (sem selecao) -> pulsa (feedback)
 *   ESC                        -> cancela
 *
 * O conjunto FICA depois de um envio rapido (encadeia alvos); o dialogo cancela ao fechar.
 */

import gsap from 'gsap';
import type { EstadoJogo } from '../domain/tipos';
import { planetaEm, pulsarPlaneta } from '../render/LinhaSelecao';
import type { DialogFrota } from '../ui/DialogFrota';
import type { Camera } from '../render/Camera';

export interface ClickOpcoes {
  somar?: boolean;   // shift (ou modo Varios ligado)
  rapido?: boolean;  // botao direito / ctrl / cmd
}

export class Selecao {
  private readonly origens = new Set<number>();
  private tweens: gsap.core.Tween[] = [];
  private aoEnvioRapido: ((origens: number[], destinoId: number) => void) | null = null;
  private aoMudar: (() => void) | null = null;

  constructor(
    private readonly estado: EstadoJogo,
    private readonly camera: Camera,
    private readonly dialog: DialogFrota,
  ) {}

  onEnvioRapido(cb: (origens: number[], destinoId: number) => void): void {
    this.aoEnvioRapido = cb;
  }

  onMudar(cb: () => void): void {
    this.aoMudar = cb;
  }

  /** Id da origem quando ha EXATAMENTE uma selecionada (botao de torre); senao null. */
  get origemId(): number | null {
    return this.origens.size === 1 ? [...this.origens][0] : null;
  }

  get ids(): number[] {
    return [...this.origens];
  }

  get quantidade(): number {
    return this.origens.size;
  }

  /** Processa um click em coords de tela. */
  processarClick(sx: number, sy: number, op: ClickOpcoes = {}): void {
    if (this.dialog.visivel()) return;

    const mundo = this.camera.telaParaMundo(sx, sy);
    const p = planetaEm(this.estado, mundo.x, mundo.y);
    const somar = !!op.somar || this.estado.modoMulti;

    if (!p) {
      if (!somar) this.cancelar();
      return;
    }

    const meu = p.donoId === this.estado.suaCor;
    if (meu) {
      // Envio rapido pra um planeta MEU = reforco
      if (op.rapido && this.origens.size > 0 && !this.origens.has(p.id)) {
        this.enviarRapido(p.id);
        return;
      }
      if (somar) {
        if (this.origens.has(p.id)) this.origens.delete(p.id);
        else this.origens.add(p.id);
        this.atualizarHighlight();
        return;
      }
      if (this.origens.size === 1 && this.origens.has(p.id)) {
        this.cancelar();
        return;
      }
      this.origens.clear();
      this.origens.add(p.id);
      this.atualizarHighlight();
      return;
    }

    // Alvo (neutro ou inimigo)
    if (this.origens.size === 0) {
      pulsarPlaneta(p);
      return;
    }
    if (op.rapido) {
      this.enviarRapido(p.id);
      return;
    }
    this.dialog.abrir(this.ids, p.id);
  }

  /** Soma ao conjunto todos os meus planetas cujo centro cai no retangulo (coords de mundo). */
  selecionarRetangulo(x1: number, y1: number, x2: number, y2: number): void {
    const minX = Math.min(x1, x2), maxX = Math.max(x1, x2);
    const minY = Math.min(y1, y2), maxY = Math.max(y1, y2);
    for (const p of this.estado.planetas.values()) {
      if (p.donoId !== this.estado.suaCor) continue;
      if (p.x >= minX && p.x <= maxX && p.y >= minY && p.y <= maxY) this.origens.add(p.id);
    }
    this.atualizarHighlight();
  }

  selecionarTodos(): void {
    this.origens.clear();
    for (const p of this.estado.planetas.values()) {
      if (p.donoId === this.estado.suaCor) this.origens.add(p.id);
    }
    this.atualizarHighlight();
  }

  /** Tira um planeta do conjunto (perdido pro inimigo, por exemplo). */
  remover(id: number): void {
    if (!this.origens.delete(id)) return;
    this.atualizarHighlight();
  }

  cancelar(): void {
    this.origens.clear();
    this.atualizarHighlight();
  }

  private enviarRapido(destinoId: number): void {
    if (this.aoEnvioRapido) this.aoEnvioRapido(this.ids, destinoId);
  }

  private atualizarHighlight(): void {
    for (const t of this.tweens) t.kill();
    this.tweens = [];
    for (const p of this.estado.planetas.values()) {
      if (p.spriteContainer) p.spriteContainer.alpha = 1;
    }
    for (const id of this.origens) {
      const p = this.estado.planetas.get(id);
      if (p?.spriteContainer) {
        this.tweens.push(gsap.to(p.spriteContainer, {
          alpha: 0.72,
          duration: 0.6,
          repeat: -1,
          yoyo: true,
          ease: 'sine.inOut',
        }));
      }
    }
    if (this.aoMudar) this.aoMudar();
  }
}
